import React, { createContext, useContext } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import theme from './theme';
import type { Theme } from './theme';

// MARK: - Theme Context
const ThemeContext = createContext<Theme>(theme);

// MARK: - Theme Provider Component
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ThemeContext.Provider value={theme}>
      <StyledThemeProvider theme={theme}>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

// MARK: - Theme Hook
export const useTheme = (): Theme => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// MARK: - Theme Utility Functions
export const getColor = (path: keyof Theme['colors'] | string): string => {
  const keys = path.split('.');
  let value: any = theme.colors;
  
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Color path "${path}" not found in theme`);
      return '#000000';
    }
  }
  
  return value;
};

export const getTypography = (path: keyof Theme['typography'] | string): string => {
  const keys = path.split('.');
  let value: any = theme.typography;
  
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Typography path "${path}" not found in theme`);
      return 'inherit';
    }
  }
  
  return value;
};

export const getSpacing = (size: keyof Theme['spacing']): string => {
  return theme.spacing[size] || '0';
};

export const getBorderRadius = (size: keyof Theme['borderRadius']): string => {
  return theme.borderRadius[size] || '0';
};

export const getShadow = (size: keyof Theme['shadows']): string => {
  return theme.shadows[size] || 'none';
};

export const getTransition = (speed: keyof Theme['transitions']): string => {
  return theme.transitions[speed] || 'none';
};

// MARK: - Styled Components Theme Access
export const themeColor = (path: string) => ({ theme }: { theme: Theme }) => {
  const keys = path.split('.');
  let value: any = theme.colors;
  
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Color path "${path}" not found in theme`);
      return '#000000';
    }
  }
  
  return value;
};

export const themeTypography = (path: string) => ({ theme }: { theme: Theme }) => {
  const keys = path.split('.');
  let value: any = theme.typography;
  
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Typography path "${path}" not found in theme`);
      return 'inherit';
    }
  }
  
  return value;
};

export const themeSpacing = (size: keyof Theme['spacing']) => ({ theme }: { theme: Theme }) => {
  return theme.spacing[size] || '0';
};

export const themeBorderRadius = (size: keyof Theme['borderRadius']) => ({ theme }: { theme: Theme }) => {
  return theme.borderRadius[size] || '0';
};

export const themeShadow = (size: keyof Theme['shadows']) => ({ theme }: { theme: Theme }) => {
  return theme.shadows[size] || 'none';
};

export const themeTransition = (speed: keyof Theme['transitions']) => ({ theme }: { theme: Theme }) => {
  return theme.transitions[speed] || 'none';
}; 